import { useMemo } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { useTabVisibilityStore } from '../../store/useTabVisibilityStore';
import { useDashboardLayoutStore } from '../../store/useDashboardLayoutStore';
import { usePublishPageTabSettings } from '../../store/usePageSettingsStore';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ROLE_LABELS, asCollaboratorRole } from '../../services/auth/permissions';
import { TicketsSummaryWidget } from '../tickets/TicketsSummaryWidget';
import { DossiersKanbanWidget } from './DossiersKanbanWidget';
import { AgendaWidget } from './AgendaWidget';
import { OrdreDuJourWidget } from './OrdreDuJourWidget';
import { MyTasksWidget } from './MyTasksWidget';
import { CabinetKpisWidget } from './CabinetKpisWidget';
import { RecentDossiersWidget } from './RecentDossiersWidget';
import { ParapheurWidget } from './ParapheurWidget';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { WidgetGrid } from './WidgetGrid';
import {
  DASHBOARD_DEFAULT_HIDDEN,
  DASHBOARD_VIEW_ID,
  dashboardTabOptions,
  resolveDashboardLayout,
  visibleDashboardWidgets,
} from './dashboardWidgets';

function getRoleLabel(role: string): string {
  const typed = asCollaboratorRole(role as never);
  return typed ? ROLE_LABELS[typed] : role;
}

function WelcomeCard() {
  const collaborator = useAuthStore((s) => s.collaborator);
  if (!collaborator) return null;

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={<WidgetCardHeader title="Message de bienvenue" />}
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-lg font-semibold text-neutral-900">
            Bienvenue, {collaborator.full_name}
          </p>
          <p className="text-sm text-neutral-500 mt-0.5">
            Vous êtes connecté en tant que
          </p>
        </div>
        <Badge
          variant={
            collaborator.role === 'admin'
              ? 'gold'
              : collaborator.role === 'lawyer'
                ? 'primary'
                : 'default'
          }
        >
          {getRoleLabel(collaborator.role)}
        </Badge>
      </div>
    </Card>
  );
}

function renderWidget(key: string) {
  switch (key) {
    case 'welcome':
      return <WelcomeCard />;
    case 'kpis':
      return <CabinetKpisWidget />;
    case 'recent-dossiers':
      return <RecentDossiersWidget />;
    case 'parapheur':
      return <ParapheurWidget />;
    case 'dossiers-kanban':
      return <DossiersKanbanWidget />;
    case 'agenda':
      return <AgendaWidget />;
    case 'ordre-du-jour':
      return <OrdreDuJourWidget />;
    case 'my-tasks':
      return <MyTasksWidget />;
    case 'tickets':
      return <TicketsSummaryWidget />;
    default:
      return null;
  }
}

export function DashboardPage() {
  const collaborator = useAuthStore((s) => s.collaborator);
  const role = asCollaboratorRole(collaborator?.role as never);
  const hiddenMap = useTabVisibilityStore((s) => s.hidden);
  const storedHidden = hiddenMap[DASHBOARD_VIEW_ID];
  const storedOrder = useDashboardLayoutStore((s) =>
    collaborator?.id ? s.orderByCollaborator[collaborator.id] : undefined,
  );
  const storedSpans = useDashboardLayoutStore((s) =>
    collaborator?.id ? s.spansByCollaborator[collaborator.id] : undefined,
  );
  const storedHeights = useDashboardLayoutStore((s) =>
    collaborator?.id ? s.heightsByCollaborator[collaborator.id] : undefined,
  );
  const storedBoxes = useDashboardLayoutStore((s) =>
    collaborator?.id ? s.boxesByCollaborator[collaborator.id] : undefined,
  );
  const setBoxes = useDashboardLayoutStore((s) => s.setBoxes);

  usePublishPageTabSettings(DASHBOARD_VIEW_ID, dashboardTabOptions(role), {
    defaultHidden: DASHBOARD_DEFAULT_HIDDEN,
    title: 'Personnaliser le tableau de bord',
    description:
      'Choisissez les blocs à afficher. Poignée gauche : déplacer. Bord droit, bord bas ou coin : largeur et hauteur.',
  });

  const visible = visibleDashboardWidgets(storedHidden, role, storedOrder);
  const visibleKey = visible.map((w) => w.key).join(',');
  const boxes = useMemo(
    () => resolveDashboardLayout(visible, storedBoxes, { spans: storedSpans, heights: storedHeights }),
    // visible change à chaque rendu : on suit ses clés.
    [visibleKey, storedBoxes, storedSpans, storedHeights],
  );

  return (
    <div className="main-container-gdm">
      <WidgetGrid
        items={visible}
        boxes={boxes}
        onCommit={(next) => {
          if (collaborator?.id) setBoxes(collaborator.id, next);
        }}
        render={renderWidget}
      />
    </div>
  );
}
